import type {
  Lootbox,
  PlayerBonus,
  PlayerCompoints,
  PlayerFreespin,
  PlayerTransaction,
  ReferralProgramRealTime,
} from 'api/common';
import { findReceivedBonus } from 'helpers/findReceivedBonus';
import { updateOrAppendById } from 'helpers/updateOrAppendById';
import produce from 'immer';

export type State = {
  bonuses: PlayerBonus[];
  bonusesPending: boolean;
  freespins: PlayerFreespin[];
  freespinsPending: boolean;
  receivedBonus: PlayerBonus | PlayerFreespin | null;
  lootboxes: Lootbox[];
  lootboxesPending: boolean;
  playerPayments: PlayerTransaction[];
  referralProgram: ReferralProgramRealTime | null;
  compPoints: PlayerCompoints | null;
};

export enum ACTION_TYPE {
  SET_LOOTBOXES = 0,
  ADD_LOOTBOX = 3,
  ADD_PAYMENT = 7,
  REFERRAL_PROGRAM = 8,
  SET_BONUSES = 9,
  SET_FREESPINS = 10,
  UPDATE_BONUSES = 11,
  UPDATE_FREESPINS = 12,
  SET_COMP_POINTS = 13,
  SET_LOOTBOXES_SETTLED = 14,
  RESET_RECEIVED_BONUS = 15,
  RESET_USER_DATA = 16,
}

export type Action =
  | {
      type: ACTION_TYPE.SET_BONUSES;
      payload: { bonuses: PlayerBonus[] };
    }
  | {
      type: ACTION_TYPE.SET_FREESPINS;
      payload: { freespins: PlayerFreespin[] };
    }
  | {
      type: ACTION_TYPE.UPDATE_BONUSES;
      payload: { bonus: PlayerBonus };
    }
  | {
      type: ACTION_TYPE.UPDATE_FREESPINS;
      payload: { freespin: PlayerFreespin };
    }
  | {
      type: ACTION_TYPE.SET_LOOTBOXES;
      payload: { lootboxes: Lootbox[] };
    }
  | {
      type: ACTION_TYPE.ADD_LOOTBOX;
      payload: { lootbox: Lootbox };
    }
  | {
      type: ACTION_TYPE.ADD_PAYMENT;
      payload: { payment: PlayerTransaction };
    }
  | {
      type: ACTION_TYPE.REFERRAL_PROGRAM;
      payload: { referralProgram: ReferralProgramRealTime };
    }
  | {
      type: ACTION_TYPE.SET_COMP_POINTS;
      payload: { compPoints: PlayerCompoints };
    }
  | {
      type: ACTION_TYPE.SET_LOOTBOXES_SETTLED;
    }
  | { type: ACTION_TYPE.RESET_USER_DATA }
  | { type: ACTION_TYPE.RESET_RECEIVED_BONUS };

type Reducer = (state: State, action: Action) => State;

export const reducer: Reducer = (state, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      case ACTION_TYPE.SET_LOOTBOXES:
        draft.lootboxes = action.payload.lootboxes;
        draft.lootboxesPending = false;
        break;
      case ACTION_TYPE.SET_LOOTBOXES_SETTLED:
        draft.lootboxesPending = false;
        break;
      case ACTION_TYPE.ADD_LOOTBOX:
        draft.lootboxes.push({ ...action.payload.lootbox });
        break;
      case ACTION_TYPE.ADD_PAYMENT:
        draft.playerPayments.push(action.payload.payment);
        break;
      case ACTION_TYPE.REFERRAL_PROGRAM:
        draft.referralProgram = action.payload.referralProgram;
        break;
      case ACTION_TYPE.SET_BONUSES: {
        const receivedBonus = findReceivedBonus(
          action.payload.bonuses,
          state.bonuses,
          !state.bonusesPending
        );

        if (receivedBonus) draft.receivedBonus = receivedBonus;

        draft.bonuses = action.payload.bonuses;
        draft.bonusesPending = false;
        break;
      }
      case ACTION_TYPE.RESET_USER_DATA:
        draft.bonuses = [];
        draft.bonusesPending = true;
        draft.freespins = [];
        draft.freespinsPending = true;
        draft.receivedBonus = null;
        draft.lootboxes = [];
        draft.lootboxesPending = true;
        draft.playerPayments = [];
        draft.compPoints = null;
        draft.referralProgram = null;
        break;
      case ACTION_TYPE.RESET_RECEIVED_BONUS:
        draft.receivedBonus = null;
        break;
      case ACTION_TYPE.SET_FREESPINS: {
        const receivedBonus = findReceivedBonus(
          action.payload.freespins,
          state.freespins,
          !state.freespinsPending
        );

        if (receivedBonus) draft.receivedBonus = receivedBonus;

        draft.freespins = action.payload.freespins;
        draft.freespinsPending = false;
        break;
      }
      case ACTION_TYPE.UPDATE_BONUSES: {
        const receivedBonus = findReceivedBonus(
          [action.payload.bonus],
          state.bonuses,
          !state.bonusesPending
        );

        if (receivedBonus) draft.receivedBonus = receivedBonus;

        draft.bonuses = updateOrAppendById(
          draft.bonuses,
          action.payload.bonus,
          'id'
        );
        break;
      }
      case ACTION_TYPE.UPDATE_FREESPINS: {
        const receivedBonus = findReceivedBonus(
          [action.payload.freespin],
          state.freespins,
          !state.freespinsPending
        );

        if (receivedBonus) draft.receivedBonus = receivedBonus;

        draft.freespins = updateOrAppendById(
          draft.freespins,
          action.payload.freespin,
          'id'
        );
        break;
      }
      case ACTION_TYPE.SET_COMP_POINTS:
        draft.compPoints = action.payload.compPoints;
        break;
    }
  });
